import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

import MarkDown from '~/components/Helpers/MarkDown'
import TertiaryLink from '~/components/UI/Buttons/TertiaryLink/TertiaryLink'

export type TimelineCardProps = CardProps & {
  content: {
    date?: string
    largeImage?: boolean
  }
  images?: boolean
}

export const cardVariants = cva('', {
  variants: {
    backgroundColour: {
      'ink-blue': 'fill-blue-d ',
      'mid-blue': 'fill-blue-m ',
      'light-blue': 'fill-blue-l',
      chartreuse: 'fill-char',
      'light-pink': 'fill-pink',
      'light-aqua': 'fill-green',
      'light-purple': 'fill-purple',
      'light-orange': 'fill-orange'
    }
  },
  defaultVariants: {
    backgroundColour: 'ink-blue'
  }
})

const TimelineCard = ({
  content,
  images,
  className,
  layoutColour
}: TimelineCardProps) => {
  const { date, copy, image, largeImage, buttons, backgroundColour } = content

  const button = buttons ? buttons[0] : null

  const foregroundColour =
    layoutColour === 'ink-blue' || layoutColour === 'mid-blue'
      ? 'white'
      : 'ink-blue'

  const previewUrl =
    isDevelopment && !image?.asset.sys.uri.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div
        className={cn('flex flex-col justify-end', images ? 'h-[277px]' : '')}
      >
        {image && (
          <div
            className={cn(
              'lg:pl-m sm:pl-s pl-xs-plus',
              largeImage ? 'max-h-[213px]' : 'mt-m max-h-[165px]'
            )}
          >
            <img
              src={`${previewUrl}${image?.asset.sys.uri}?w=200&h=250&fit=crop&q=40&f=webp`}
              alt={image?.altText}
              className='aspect-3/4 h-full w-auto object-cover'
            />
          </div>
        )}
        <svg
          width='100%'
          height='100%'
          className='lg:h-xl lg:min-h-xl sm:h-l sm:min-h-l h-[40px] min-h-[40px]'
        >
          <rect
            height={20}
            className={cn(
              cardVariants({ backgroundColour }),
              'sm:h-s lg:h-m w-[calc(100%-20px)] sm:w-[calc(100%-24px)] lg:w-[calc(100%-32px)]'
            )}
          />
          <rect
            width={20}
            height={320}
            className={cn(
              cardVariants({ backgroundColour }),
              'lg:h-m lg:w-m sm:h-s sm:w-s translate-x-[calc(100%-20px)] translate-y-[20px] sm:translate-x-[calc(100%-24px)] sm:translate-y-[24px] lg:translate-x-[calc(100%-32px)] lg:translate-y-[32px]'
            )}
          />
        </svg>
      </div>
      <div className='gap-xs p-xs-plus pr-l sm:p-s sm:pr-l lg:p-m lg:pr-xl flex grow flex-col !py-0'>
        <h2
          className={cn(
            'pb-xs text-h2-sm sm:text-h2-md lg:text-h2-lg border-b-1 font-serif',
            [
              'ink-blue',
              'mid-blue'.includes(layoutColour ?? '')
                ? 'border-white/40'
                : 'border-blue-d-60'
            ]
          )}
        >
          {date}
        </h2>
        <MarkDown content={copy} className='grow' />
        {button && (
          <div className='mt-xs w-fit'>
            <TertiaryLink data={button} colour={foregroundColour} />
          </div>
        )}
      </div>
    </div>
  )
}

export default TimelineCard
